import { awaitTurnstileToken, getTurnstileToken } from 'js/src/agent/turnstileManager';
import { client } from 'js/src/client';
import { ShowRequestErrorModal } from 'js/src/components/common/RequestErrorModal';
import { INFINITE_TOAST_DURATION, Toast } from 'js/src/components/common/Toast';
import { ApiError, isUndefined } from 'js/src/libs/consts';
import { waitForever, waitForMillis } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import uuid from 'js/src/libs/uuid';
import Cookies from 'js-cookie';

/*
 * Duplicate Request Detection (debug mode only)
 * Warns when the same API request is made multiple times within a short window
 */
type RequestRecord = {
    timestamp: number;
    count: number;
    stack: string;
};

const recentRequests = new Map<string, RequestRecord>();
const DUPLICATE_WINDOW_MS = 1000; // Warn if same request made within 1s

type ExcludedEndpoint = {
    endpoint: string;
    caller?: string; // If omitted, excludes all calls to this endpoint
};

const EXCLUDED_ENDPOINTS: ExcludedEndpoint[] = [
    { endpoint: '/api/task-results/' },
    { endpoint: '/api/async-operations/' },
    { endpoint: '/api/team-settings/', caller: 'usePollForSeatPurchased' },
];

function getRequestKey(method: string, url: string, body: string | null): string {
    return `${method}:${url}:${body || ''}`;
}

// only show traces in source code, exclude traces from this file
function cleanStack(stack: string): string {
    return stack
        .split('\n')
        .filter(
            (line) =>
                !line.includes('at ') ||
                (line.includes('js/src/') && !line.includes('configure-openapi')),
        )
        .join('\n');
}

function isExcludedRequest(url: string, stack: string): boolean {
    return EXCLUDED_ENDPOINTS.some(
        ({ endpoint, caller }) => url.includes(endpoint) && (!caller || stack.includes(caller)),
    );
}

function checkForDuplicateRequest(request: Request, bodyText: string | null): void {
    const originalLimit = Error.stackTraceLimit;
    Error.stackTraceLimit = 100;
    const stack = cleanStack(new Error().stack || '');
    Error.stackTraceLimit = originalLimit;

    if (isExcludedRequest(request.url, stack)) {
        return;
    }

    const key = getRequestKey(request.method, request.url, bodyText);
    const now = Date.now();
    const existing = recentRequests.get(key);

    for (const [k, record] of recentRequests) {
        if (now - record.timestamp > DUPLICATE_WINDOW_MS) {
            recentRequests.delete(k);
        }
    }

    if (existing && now - existing.timestamp < DUPLICATE_WINDOW_MS) {
        const newCount = existing.count + 1;
        // eslint-disable-next-line no-console
        console.warn(
            `[Duplicate Request Detected] ${request.method} ${request.url}\n` +
                `Request count: ${newCount}, last was ${now - existing.timestamp}ms ago`,
            `Original call stack:\n${existing.stack}\n` + `Current call stack:\n${stack}`,
        );

        recentRequests.set(key, {
            timestamp: now,
            count: newCount,
            stack: existing.stack,
        });
    } else {
        recentRequests.set(key, {
            timestamp: now,
            count: 1,
            stack,
        });
    }
}

function getCSRFToken(): string {
    const csrfToken = Cookies.get('csrftoken');
    return isUndefined(csrfToken) ? '' : csrfToken;
}

function getServerData(): any {
    return (window as any).SERVER;
}

function getWorkspaceId(): string {
    const SERVER = getServerData();
    return isUndefined(SERVER?.USER?.workspace_id) ? '' : `${SERVER.USER.workspace_id}`;
}

/*
 * Compress a string using the CompressionStream API
 */
async function compressWithCompressionStream(input: string): Promise<Blob> {
    const encoder = new TextEncoder();
    const inputStream = new ReadableStream({
        start(controller) {
            controller.enqueue(encoder.encode(input));
            controller.close();
        },
    });

    const compressedStream = inputStream.pipeThrough(new CompressionStream('gzip'));
    const response = new Response(compressedStream);
    return response.blob();
}

/*
 * Compress large request bodies (saving 90-95% bandwidth)
 */
export async function gzipBigRequests({
    request,
    thresholdBytes = 300 * 1024,
    bodyText,
}: {
    request: Request;
    thresholdBytes?: number;
    bodyText?: string | null;
}): Promise<Request> {
    try {
        const method = request.method.toUpperCase();

        if (['POST', 'PUT', 'PATCH'].includes(method)) {
            const originalBody = bodyText || (await request.clone().text());
            const encoded = new TextEncoder().encode(originalBody);

            if (encoded.length > thresholdBytes) {
                const compressedBlob = await compressWithCompressionStream(originalBody);

                const newHeaders = new Headers(request.headers);
                newHeaders.set('Content-Encoding', 'gzip');

                return new Request(request.url, {
                    method,
                    headers: newHeaders,
                    body: compressedBlob,
                    credentials: request.credentials,
                    cache: request.cache,
                    redirect: request.redirect,
                    referrer: request.referrer,
                    referrerPolicy: request.referrerPolicy,
                    integrity: request.integrity,
                    keepalive: request.keepalive,
                    signal: request.signal,
                    mode: request.mode,
                });
            }
        }

        return request;
    } catch (error) {
        ErrorReporter.capture(error);
        return request;
    }
}

export class FetchRetry {
    private static ongoingRetries: Set<string> = new Set();

    public static isNetworkError(error: unknown): boolean {
        if (!(error instanceof TypeError)) {
            return false;
        }

        const message = error.message.trim().toLowerCase();

        // Check for common network error messages across browsers
        return (
            message.includes('failed to fetch') || // Chromium desktop
            message.includes('load failed') || // iOS Safari/Chrome Mobile
            message.includes('networkerror when attempting to fetch resource') // Firefox
        );
    }

    public static async fetchWithRetry(
        input: RequestInfo | URL,
        init?: RequestInit,
        maxRetries = 3,
    ): Promise<Response> {
        const requestGuid = uuid();
        const retryPromise = (async (): Promise<Response> => {
            for (let attempt = 0; attempt <= maxRetries; attempt++) {
                try {
                    let requestInput: RequestInfo | URL;
                    if (input instanceof Request) {
                        // clone Request object to avoid "already been used" error
                        requestInput = input.clone();
                    } else {
                        requestInput = input;
                    }

                    // use sentry's fetch function
                    const response = await fetch(requestInput, init);
                    if (
                        attempt > 0 &&
                        FetchRetry.ongoingRetries.has(requestGuid) &&
                        [...FetchRetry.ongoingRetries][0] === requestGuid
                    ) {
                        Toast.info(
                            'Successfully reconnected!',
                            'Connection Established',
                            'network-error-toast',
                            3000,
                        );
                    }
                    FetchRetry.ongoingRetries.delete(requestGuid);
                    return response;
                } catch (error) {
                    if (FetchRetry.isNetworkError(error)) {
                        FetchRetry.ongoingRetries.add(requestGuid);
                        if (attempt < maxRetries) {
                            // Show countdown for retry attempts
                            const waitDurationSeconds = Math.pow(3, attempt);

                            for (
                                let remainingSeconds = waitDurationSeconds;
                                remainingSeconds > 0;
                                remainingSeconds--
                            ) {
                                if (
                                    FetchRetry.ongoingRetries.has(requestGuid) &&
                                    [...FetchRetry.ongoingRetries][0] === requestGuid
                                ) {
                                    Toast.error(
                                        `Connection failed. Retrying in ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}...`,
                                        'Connection Issue',
                                        'network-error-toast',
                                        INFINITE_TOAST_DURATION,
                                    );
                                }
                                await waitForMillis(1000);
                            }
                        }

                        if (attempt === maxRetries) {
                            ShowRequestErrorModal();
                            // never resolve this fetch, let the user reload manually with RequestErrorModal.
                            Toast.removeAll();
                            await waitForever();
                        }
                    } else {
                        FetchRetry.ongoingRetries.delete(requestGuid);
                        throw error;
                    }
                }
            }
            FetchRetry.ongoingRetries.delete(requestGuid);
            throw new Error('Unreachable code reached.');
        })();

        return retryPromise;
    }
}

client.interceptors.request.use(async (request) => {
    request.headers.set('Content-Type', 'application/json');

    // add the csrf token to the headers in all requests, so that django's
    // csrf protection will not reject requests sent via the OpenAPI client
    request.headers.set('X-CSRFToken', getCSRFToken());

    // add the Lumen5 workspace id to the headers in all requests, so that
    // the backend can determine which workspace the request is for
    request.headers.set('X-LUMEN5-WORKSPACE', getWorkspaceId());

    // Attach Cloudflare Turnstile token for bot verification.
    // Turnstile-protected endpoints block until a token is available; all other
    // requests attach the token opportunistically without waiting.
    const turnstileProtectedPaths = [
        '/api/background-removal/',
        '/api/video-generation/',
        '/api/image-generation/',
    ];
    const needsTurnstileAwait = turnstileProtectedPaths.some((path) => request.url.includes(path));
    const turnstileToken = needsTurnstileAwait ? await awaitTurnstileToken() : getTurnstileToken();
    if (turnstileToken) {
        request.headers.set('X-Turnstile-Token', turnstileToken);
    }

    const bodyText = request.body ? await request.clone().text() : null;
    // Check for duplicate requests (debug mode only)
    if (getServerData()?.DEBUG) {
        // Read body before gzipping so we can compare raw payloads
        checkForDuplicateRequest(request, bodyText);
    }

    // for large requests, we gzip the body to reduce the size of the request
    return gzipBigRequests({ request, bodyText });
});

client.interceptors.error.use(async (error: any, response, request, opts) => {
    if (
        response.status === 403 &&
        error?.detail === 'Authentication credentials were not provided.'
    ) {
        Navigation.reloadPage();
        Toast.error(
            'You have been logged out. Please refresh and log in again.',
            undefined,
            'authentication-credentials-were-not-provided',
            INFINITE_TOAST_DURATION,
        );
        await waitForever();
    }
    if (!(error instanceof Error)) {
        return new ApiError(response.status, error, opts.url);
    }
    return error;
});

client.setConfig({
    throwOnError: true,
});
